import React from "react";

/**
 * CardIllustration — a set of consistent, on-brand SVG illustrations used by the
 * "What to look for in an ERP partner" cards (and reusable elsewhere).
 *
 * All illustrations share: 400x260 viewBox, the teal token palette, the same
 * soft rounded background, the same stroke weight and visual density — so the
 * card row reads as one cohesive set instead of mismatched stock photos/icons.
 */

const PALETTE = {
  bg: "#EAF1F1",        // soft teal-tint background
  ink: "#1D6068",       // brand teal
  inkSoft: "#5B8A8F",   // muted teal
  accent: "#C4543B",    // signal/contrast (sparingly)
  line: "#9DBCBE",      // light line
  white: "#FFFFFF",
};

function Frame({ children }) {
  return (
    <svg
      viewBox="0 0 400 260"
      width="100%"
      height="100%"
      preserveAspectRatio="xMidYMid slice"
      role="img"
      xmlns="http://www.w3.org/2000/svg"
    >
      <rect width="400" height="260" fill={PALETTE.bg} />
      {children}
    </svg>
  );
}

const ART = {
  // Proven expertise & industry experience — award/medal + bars
  expertise: (
    <Frame>
      <g fill="none" stroke={PALETTE.ink} strokeWidth="6" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="200" cy="108" r="46" fill={PALETTE.white} />
        <path d="M200 86l7 14 15 2-11 11 3 15-14-8-14 8 3-15-11-11 15-2z" fill={PALETTE.ink} stroke="none" />
        <path d="M176 150l-12 46 36-20 36 20-12-46" />
      </g>
      <g fill={PALETTE.inkSoft}>
        <rect x="70" y="150" width="22" height="50" rx="4" />
        <rect x="100" y="130" width="22" height="70" rx="4" />
        <rect x="278" y="135" width="22" height="65" rx="4" />
        <rect x="308" y="118" width="22" height="82" rx="4" />
      </g>
    </Frame>
  ),

  // Client success & case studies — document with check + quote
  casestudy: (
    <Frame>
      <g>
        <rect x="118" y="56" width="120" height="150" rx="10" fill={PALETTE.white} stroke={PALETTE.ink} strokeWidth="6" />
        <rect x="140" y="84" width="64" height="8" rx="4" fill={PALETTE.line} />
        <rect x="140" y="104" width="76" height="8" rx="4" fill={PALETTE.line} />
        <rect x="140" y="124" width="50" height="8" rx="4" fill={PALETTE.line} />
        <circle cx="244" cy="170" r="34" fill={PALETTE.ink} />
        <path d="M230 170l10 10 20-22" fill="none" stroke={PALETTE.white} strokeWidth="7" strokeLinecap="round" strokeLinejoin="round" />
      </g>
    </Frame>
  ),

  // Scalability & future readiness — stacked layers / growth arrow
  scalability: (
    <Frame>
      <g fill="none" stroke={PALETTE.ink} strokeWidth="6" strokeLinecap="round" strokeLinejoin="round">
        <rect x="120" y="150" width="160" height="34" rx="8" fill={PALETTE.white} />
        <rect x="138" y="116" width="124" height="32" rx="8" fill={PALETTE.white} />
        <rect x="156" y="84" width="88" height="30" rx="8" fill={PALETTE.white} />
      </g>
      <path d="M286 120l34-34m0 0h-22m22 0v22" fill="none" stroke={PALETTE.accent} strokeWidth="7" strokeLinecap="round" strokeLinejoin="round" />
    </Frame>
  ),

  // Structured project execution — gantt / milestones
  execution: (
    <Frame>
      <g>
        <line x1="80" y1="60" x2="80" y2="208" stroke={PALETTE.line} strokeWidth="4" />
        <rect x="96" y="74" width="150" height="20" rx="10" fill={PALETTE.inkSoft} />
        <rect x="130" y="104" width="170" height="20" rx="10" fill={PALETTE.ink} />
        <rect x="110" y="134" width="120" height="20" rx="10" fill={PALETTE.inkSoft} />
        <rect x="160" y="164" width="150" height="20" rx="10" fill={PALETTE.ink} />
      </g>
      <g fill={PALETTE.accent}>
        <circle cx="246" cy="84" r="9" />
        <circle cx="300" cy="114" r="9" />
        <circle cx="230" cy="144" r="9" />
        <circle cx="310" cy="174" r="9" />
      </g>
    </Frame>
  ),

  // Change management & user adoption — people + upward adoption curve
  adoption: (
    <Frame>
      {/* adoption curve */}
      <path d="M70 196 C140 196 150 120 210 110 C270 100 300 80 330 70"
        fill="none" stroke={PALETTE.ink} strokeWidth="6" strokeLinecap="round" />
      <path d="M330 70l-4 -16m4 16l-16 4" fill="none" stroke={PALETTE.ink} strokeWidth="6" strokeLinecap="round" strokeLinejoin="round" />
      {/* three people adopting */}
      <g>
        <circle cx="110" cy="150" r="18" fill={PALETTE.inkSoft} />
        <path d="M84 196c0-16 12-26 26-26s26 10 26 26z" fill={PALETTE.inkSoft} />
        <circle cx="190" cy="128" r="18" fill={PALETTE.ink} />
        <path d="M164 174c0-16 12-26 26-26s26 10 26 26z" fill={PALETTE.ink} />
        <circle cx="270" cy="104" r="18" fill={PALETTE.accent} />
        <path d="M244 150c0-16 12-26 26-26s26 10 26 26z" fill={PALETTE.accent} />
      </g>
    </Frame>
  ),

  // Reliable support & strong partnerships — handshake / shield support
  support: (
    <Frame>
      <g fill="none" stroke={PALETTE.ink} strokeWidth="6" strokeLinecap="round" strokeLinejoin="round">
        <path d="M200 60l64 22v44c0 44-34 64-64 74-30-10-64-30-64-74V82z" fill={PALETTE.white} />
        <path d="M176 130l16 16 34-34" stroke={PALETTE.ink} />
      </g>
      <g fill={PALETTE.inkSoft}>
        <circle cx="96" cy="96" r="13" />
        <circle cx="304" cy="96" r="13" />
      </g>
      <path d="M96 110v18c0 8 6 14 14 14h20M304 110v18c0 8-6 14-14 14h-20"
        fill="none" stroke={PALETTE.line} strokeWidth="5" strokeLinecap="round" />
    </Frame>
  ),
};

export default function CardIllustration({ name, className }) {
  const art = ART[name] || ART.expertise;
  return <div className={className}>{art}</div>;
}
